// The desk's cards: what a tool returned, drawn in the conversation beside the desk's words (picks,
// the market, a company's results, a chart…). Every figure on a card is the tool's own result, sent
// by the API (agent.Component) or computed here by the chart's engine; the model writes none of them.
// Each card's buttons carry the conversation on: ask the desk about a row, open its chart, plan it.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRightIcon, CandlestickChartIcon, ExternalLinkIcon, FileTextIcon } from 'lucide-react';
import { NSE, PATTERN_RECORD } from '@planb/core';
import { HistoryChart } from '@/charts/history-chart';
import { setupStatus, useChartHistory, useOverlays, type ChartHistory } from './chart-history';
import { Link } from '@/lib/nav';
import { cn } from '@/lib/utils';
import type { DeskCardData } from '@/lib/desk/client';
import { DeskIcon } from './market-ui';

export type { DeskCardData };
/** What a card's buttons do: ask the desk a follow-up, or draw a stock's chart in the conversation. */
export interface CardActions { ask: (q: string) => void; chart: (symbol: string) => void }
/** The desk page's hold on its chart cards: told when one has loaded, and the plan marks to draw. */
export interface ChartProps { onReady?: (symbol: string, h: ChartHistory) => void; marks?: { price: number; label: string; kind: string }[] }

// A card reads the tool's JSON as sent.
type J = Record<string, any>;
const arr = (v: unknown): J[] => (Array.isArray(v) ? v as J[] : []);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const px = (v: unknown) => (num(v) === null ? '—' : `₹${NSE.formatPrice(num(v)!)}`);
const pct = (v: unknown, digits = 2) => (num(v) === null ? '—' : `${num(v)! >= 0 ? '+' : ''}${num(v)!.toFixed(digits)}%`);
const tone = (v: unknown) => (num(v) === null ? '' : num(v)! > 0 ? 'text-bull' : num(v)! < 0 ? 'text-bear' : '');
const cr = (v: unknown) => (num(v) === null ? '—' : `₹${num(v)!.toLocaleString('en-IN', { maximumFractionDigits: 0 })} cr`);
const isoDay = (d: unknown) => (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d) ? new Date(`${d.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : String(d ?? ''));

/** A card's frame: a title row, the body, and where its figures came from (with their time). */
function Frame({ icon, title, right, source, children, className }: { icon?: React.ReactNode; title: React.ReactNode; right?: React.ReactNode; source?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn('overflow-hidden rounded-xl border bg-background text-[12.5px]', className)}>
      <header className="flex items-center justify-between gap-3 border-b bg-tint/60 px-3 py-2">
        <span className="flex min-w-0 items-center gap-1.5 font-semibold text-ink">{icon}<span className="truncate">{title}</span></span>
        {right && <span className="shrink-0 text-[11px] text-muted-foreground">{right}</span>}
      </header>
      <div className="p-3">{children}</div>
      {source && <footer className="border-t px-3 py-1.5 text-[10.5px] text-muted-foreground">{source}</footer>}
    </section>
  );
}

const Chip = ({ onClick, children, title }: { onClick: () => void; children: React.ReactNode; title?: string }) => (
  <button type="button" onClick={onClick} title={title}
    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-brand-strong hover:text-brand-strong">{children}</button>
);
const Stat = ({ label, value, sub, cls }: { label: string; value: React.ReactNode; sub?: React.ReactNode; cls?: string }) => (
  <div className="min-w-0 rounded-lg border bg-card px-2.5 py-2">
    <div className="text-[10.5px] tracking-wide text-muted-foreground uppercase">{label}</div>
    <div className={cn('num text-[15px] font-semibold text-ink', cls)}>{value}</div>
    {sub && <div className="text-[10.5px] text-muted-foreground">{sub}</div>}
  </div>
);
const freshness = (d: J) => {
  const a = d.asOf as J | undefined;
  if (!a || typeof a !== 'object') return null;
  return a.stale ? <span className="text-warn-ink">Stale: {String(a.note ?? `fetched ${a.fetched}`)}</span> : <>fetched {String(a.fetched)} IST</>;
};

/** One card, by kind; an unknown kind draws nothing (the desk's words still say it). */
export function DeskCard({ card, act, chart }: { card: DeskCardData; act: CardActions; chart?: ChartProps }) {
  const d = card.data as J;
  switch (card.kind) {
    case 'picks': return <PicksCard d={d} act={act} />;
    case 'momentum': return <MomentumCard d={d} act={act} />;
    case 'market': return <MarketCard d={d} act={act} />;
    case 'board': return <BoardCard d={d} act={act} />;
    case 'results': return <ResultsCard d={d} />;
    case 'valuation': return <ValuationCard d={d} />;
    case 'shareholding': return <ShareholdingCard d={d} />;
    case 'meetings': return <MeetingsCard d={d} />;
    case 'actions': return <ActionsCard d={d} />;
    case 'filings': return <FilingsCard d={d} />;
    case 'paper': return <PaperCard d={d} act={act} />;
    case 'candle': return <CandleCard d={d} />;
    case 'chart': return <ChartCard symbol={String(d.symbol ?? '')} act={act} {...chart} />;
    default: return null;
  }
}

function GroupBadge({ g, edge }: { g: string; edge: boolean }) {
  return <span title={edge ? 'A group with a measured edge recently' : 'No measured edge recently'} className={cn('inline-grid size-5 shrink-0 place-items-center rounded text-[11px] font-semibold', edge ? 'bg-bull/15 text-bull' : 'bg-muted text-muted-foreground')}>{g}</span>;
}

function PicksCard({ d, act }: { d: J; act: CardActions }) {
  const top = arr(d.top);
  const plan = d.plan as J | null;
  const counts = (d.counts ?? {}) as J;
  return (
    <Frame icon={<DeskIcon className="size-3.5 text-brand-strong" />} title="Best-tested setups now"
      right={`${num(counts.openTrends) ?? 0} in a trend · ${num(counts.newSignalsToday) ?? 0} new today · ${num(d.scanned) ?? '—'} scanned`}
      source={<>New-high breakout rule (entry at the next open, trailing stop 10 × ATR(20)), ranked by measured group. {String(d.tested ?? '')}</>}>
      {d.headline && <p className="mb-2.5 text-muted-foreground">{String(d.headline)}</p>}
      {top.length === 0 && <p className="text-muted-foreground">No stock in a group with a measured edge right now.</p>}
      <div className="space-y-2">
        {top.map((x) => {
          const s = (x.stock ?? {}) as J, rs = (x.riskSized ?? {}) as J;
          const sym = String(s.symbol ?? '');
          const fresh = num(s.sessionsSinceEntry) !== null && s.sessionsSinceEntry < 0;
          return (
            <div key={sym} className="rounded-lg border bg-card p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <GroupBadge g={String(s.group ?? '?')} edge={!!s.measuredEdge} />
                <Link href={`/stock/${encodeURIComponent(sym)}`} className="font-semibold text-ink hover:text-brand-strong">{sym}</Link>
                <span className={cn('rounded px-1.5 py-0.5 text-[10.5px] font-medium', fresh ? 'bg-bull-soft text-bull' : 'bg-muted text-muted-foreground')}>{fresh ? 'new signal' : `trend since ${isoDay(s.breakoutSince)}`}</span>
                <span className="num ml-auto text-right text-ink">{px(s.lastClose)}{s.livePrice ? <span className="block text-[10.5px] text-muted-foreground">{s.forming ? 'live, not a close' : `close · live ${px(s.livePrice)}`}</span> : null}</span>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11.5px] sm:grid-cols-4">
                <span className="text-muted-foreground">Trailing stop <b className="num text-warn-ink">{px(s.trailingStop)}</b></span>
                <span className="text-muted-foreground">To stop <b className="num text-ink">{pct(-Math.abs(num(s.distanceToStopPct) ?? 0), 1)}</b></span>
                <span className="text-muted-foreground">Momentum <b className="num text-ink">{num(s.momentumRank) ? `#${s.momentumRank} of ${s.momentumOf}` : '—'}</b></span>
                <span className="text-muted-foreground">Since entry <b className={cn('num', tone(s.gainSinceEntryPct))}>{fresh ? '—' : pct(s.gainSinceEntryPct, 1)}</b></span>
              </div>
              {num(rs.qty) !== null && <p className="mt-1 text-[11px] text-muted-foreground">{rs.qty > 0
                ? <>Risk-sized: {rs.qty} share{rs.qty > 1 ? 's' : ''} ({String(rs.position_value ?? '')}), a stop-out loses {String(rs.risk_money ?? '')}.</>
                : <>At your risk per trade not one share fits: one share risks {px(rs.risk_per_share)} to this rule&apos;s wide stop.</>}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Chip onClick={() => act.chart(sym)}><CandlestickChartIcon className="size-3" />Chart</Chip>
                <Chip onClick={() => act.ask(`Why does ${sym} qualify, and what would invalidate it?`)}>Why it qualifies</Chip>
                <Chip onClick={() => act.ask(`Plan a paper trade for ${sym} by the breakout rule`)}>Plan on paper</Chip>
              </div>
            </div>
          );
        })}
      </div>
      {plan?.line && <p className="mt-2.5 rounded-lg bg-brand-soft/60 px-2.5 py-2 text-[11.5px] text-ink">{String(plan.line)}</p>}
    </Frame>
  );
}

function MomentumCard({ d, act }: { d: J; act: CardActions }) {
  const rows = arr(d.top20);
  return (
    <Frame title="Momentum top 20" right={`of ${num(d.ranked) ?? '—'} ranked`} source={String(d.note ?? '')}>
      <ol className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
        {rows.map((r) => (
          <li key={String(r.symbol)} className="flex items-center gap-1.5">
            <span className="num w-5 text-right text-[11px] text-muted-foreground">{r.rank}</span>
            <button type="button" onClick={() => act.chart(String(r.symbol))} className="truncate font-medium text-ink hover:text-brand-strong">{String(r.symbol)}</button>
          </li>
        ))}
      </ol>
    </Frame>
  );
}

function MarketCard({ d, act }: { d: J; act: CardActions }) {
  const m = (d.mood ?? d) as J;
  const b = (m.breadth ?? null) as J | null;
  const adv = num(b?.advances) ?? 0, dec = num(b?.declines) ?? 0;
  return (
    <Frame title="The market" right={isoDay(m.date)} source={String(d.source ?? 'NSE end-of-day index data')}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="NIFTY 50" value={num(m.nifty50)?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) ?? '—'} sub={<span className={tone(m.nifty50ChangePct)}>{pct(m.nifty50ChangePct)}</span>} />
        <Stat label="India VIX" value={num(m.indiaVix)?.toFixed(2) ?? '—'} sub={<><span className={tone(-(num(m.indiaVixChangePct) ?? 0))}>{pct(m.indiaVixChangePct, 1)}</span>{num(m.indiaVixHigherThanPctOfSessions5y) !== null && ` · above ${Math.round(m.indiaVixHigherThanPctOfSessions5y)}% of 5y`}</>} />
        <Stat label="NIFTY 50 P/E" value={num(m.nifty50PE)?.toFixed(2) ?? '—'} sub={num(m.nifty50PELowerThanPctOfSessions) !== null ? `below ${Math.round(m.nifty50PELowerThanPctOfSessions)}% since ${m.pePercentileSince ?? ''}` : undefined} />
        <Stat label="Above 200-day" value={b ? `${Math.round(num(b.above200DayAvgPct) ?? 0)}%` : '—'} sub={b && num(b.above200DayAvgPctAMonthAgo) !== null ? `${Math.round(b.above200DayAvgPctAMonthAgo)}% a month ago` : undefined} />
      </div>
      {b && (
        <div className="mt-2.5">
          <div className="mb-1 flex justify-between text-[11px] text-muted-foreground"><span><b className="text-bull">{adv}</b> up</span><span>breadth on {isoDay(b.date)}, {b.stocks} NSE 500 stocks</span><span><b className="text-bear">{dec}</b> down</span></div>
          <div className="flex h-1.5 overflow-hidden rounded-full bg-muted"><div className="bg-bull" style={{ width: `${(adv / Math.max(1, adv + dec)) * 100}%` }} /><div className="flex-1 bg-bear/80" /></div>
          <p className="mt-1 text-[11px] text-muted-foreground">{num(b.new52wHighs) ?? 0} new 52-week highs · {num(b.new52wLows) ?? 0} lows</p>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Chip onClick={() => act.ask('Which sectors are leading today?')}>Sectors today</Chip>
        <Chip onClick={() => act.ask('Show me the best-tested setups now')}>Best-tested setups</Chip>
      </div>
    </Frame>
  );
}

/** The Market Flow board's reading (read_market), drawn from the same numbers the desk read. */
function BoardCard({ d, act }: { d: J; act: CardActions }) {
  const sectors = arr(d.sectorsStrongestFirst);
  const max = Math.max(0.5, ...sectors.map((s) => Math.abs(num(s.avgMovePct) ?? 0)));
  const br = (d.breadth ?? {}) as J;
  const list = (title: string, rows: J[]) => (
    <div>
      <p className="mb-1 text-[11px] font-medium text-muted-foreground">{title}</p>
      {rows.map((r) => (
        <button key={String(r.symbol)} type="button" onClick={() => act.ask(`Why is ${r.symbol} moving today?`)} className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-muted">
          <span className="truncate font-medium text-ink">{String(r.symbol)}</span>
          <span className="num text-[11.5px]"><span className={tone(r.changePct)}>{pct(r.changePct)}</span> <span className="text-muted-foreground">{String(r.volumeVsUsual ?? '')}</span></span>
        </button>
      ))}
    </div>
  );
  return (
    <Frame title={`${d.index ?? 'Market'} now`} right={`${d.session ?? ''} ${d.asOf ?? ''}`} source={String(d.source ?? '')}>
      <p className="mb-2 text-[11.5px] text-muted-foreground"><b className="text-bull">{num(br.up) ?? 0}</b> up · <b className="text-bear">{num(br.down) ?? 0}</b> down of {num(d.stocks) ?? '—'}</p>
      <div className="space-y-1">
        {sectors.slice(0, 8).map((s) => {
          const v = num(s.avgMovePct) ?? 0;
          return (
            <div key={String(s.sector)} className="grid grid-cols-[minmax(0,9rem)_1fr_3.5rem] items-center gap-2 text-[11.5px]">
              <span className="truncate text-ink">{String(s.sector)}</span>
              <div className="relative h-1.5 rounded-full bg-muted"><div className={cn('absolute inset-y-0 rounded-full', v >= 0 ? 'left-1/2 bg-bull' : 'right-1/2 bg-bear')} style={{ width: `${(Math.abs(v) / max) * 50}%` }} /></div>
              <span className={cn('num text-right', tone(v))}>{pct(v)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">{list('Strongest', arr(d.strongest))}{list('Weakest', arr(d.weakest))}</div>
    </Frame>
  );
}

function ResultsCard({ d }: { d: J }) {
  const qs = arr(d.quarters);
  const max = Math.max(1, ...qs.map((q) => Math.abs(num(q.netProfitCr) ?? 0)));
  const yoy = (d.latestVsYearAgoPct ?? {}) as J;
  return (
    <Frame title="Quarterly results" right={qs[0] ? `${qs[0].basis} · latest ${qs[0].quarterEnded}` : ''} source={<>{String(d.source ?? '')}{freshness(d) && <> · {freshness(d)}</>}</>}>
      {num(yoy.revenue) !== null && <p className="mb-2 text-[11.5px] text-muted-foreground">Latest quarter against {yoy.yearAgoQuarter}: revenue <b className={tone(yoy.revenue)}>{pct(yoy.revenue, 1)}</b>, net profit <b className={tone(yoy.netProfit)}>{pct(yoy.netProfit, 1)}</b>.</p>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-[11.5px]">
          <thead className="text-left text-[10.5px] tracking-wide text-muted-foreground uppercase"><tr><th className="py-1 font-medium">Quarter</th><th className="font-medium">Revenue</th><th className="font-medium">Net profit</th><th className="font-medium">EPS</th><th className="w-24" /></tr></thead>
          <tbody>
            {qs.map((q) => (
              <tr key={String(q.quarterEnded)} className="border-t border-border/60">
                <td className="py-1 text-ink">{String(q.quarterEnded)}</td>
                <td className="num">{cr(q.revenueCr)}</td>
                <td className={cn('num', tone(q.netProfitCr))}>{cr(q.netProfitCr)}</td>
                <td className="num">{num(q.eps)?.toFixed(2) ?? '—'}</td>
                <td><div className="h-1.5 rounded-full bg-muted"><div className={cn('h-full rounded-full', (num(q.netProfitCr) ?? 0) >= 0 ? 'bg-bull' : 'bg-bear')} style={{ width: `${(Math.abs(num(q.netProfitCr) ?? 0) / max) * 100}%` }} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Frame>
  );
}

function ValuationCard({ d }: { d: J }) {
  const v = (d.valuation ?? {}) as J;
  const lo = num(v.yearLow), hi = num(v.yearHigh);
  return (
    <Frame title="Valuation" right={String(v.industry ?? '')} source={<>{String(d.source ?? 'NSE quote')}{v.asOf && ` as of ${v.asOf}`}{freshness(d) && <> · {freshness(d)}</>}</>}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="P/E" value={num(v.pe)?.toFixed(2) ?? '—'} sub={num(v.sectorPe) !== null ? `sector ${v.sectorPe.toFixed(2)}` : undefined} />
        <Stat label="Market cap" value={cr(v.marketCapCr)} sub={num(v.freeFloatCapCr) !== null ? `free float ${cr(v.freeFloatCapCr)}` : undefined} />
        <Stat label="Delivery" value={num(v.deliveryPct) !== null ? `${v.deliveryPct.toFixed(1)}%` : '—'} sub="of today's volume" />
        <Stat label="Volatility" value={num(v.annualVolatilityPct) !== null ? `${v.annualVolatilityPct.toFixed(1)}%` : '—'} sub="annual" />
      </div>
      {lo !== null && hi !== null && <p className="mt-2 text-[11.5px] text-muted-foreground">52-week range {px(lo)} ({String(v.yearLowOn ?? '')}) to {px(hi)} ({String(v.yearHighOn ?? '')}).</p>}
    </Frame>
  );
}

function ShareholdingCard({ d }: { d: J }) {
  const ps = arr(d.patterns);
  return (
    <Frame title="Shareholding" right={num(d.promoterChangePts) !== null ? <>promoters <span className={tone(d.promoterChangePts)}>{d.promoterChangePts >= 0 ? '+' : ''}{d.promoterChangePts} pts</span> since the last filing</> : ''} source={<>{String(d.source ?? '')}{freshness(d) && <> · {freshness(d)}</>}</>}>
      <div className="space-y-1">
        {ps.slice(0, 6).map((p) => (
          <div key={String(p.asOf)} className="grid grid-cols-[6.5rem_1fr_3.5rem] items-center gap-2 text-[11.5px]">
            <span className="text-muted-foreground">{String(p.asOf)}</span>
            <div className="flex h-1.5 overflow-hidden rounded-full bg-muted"><div className="bg-brand-strong" style={{ width: `${num(p.promoterPct) ?? 0}%` }} /></div>
            <span className="num text-right text-ink">{num(p.promoterPct)?.toFixed(2) ?? '—'}%</span>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[10.5px] text-muted-foreground">Promoter and promoter group holding, % of shares.</p>
    </Frame>
  );
}

function MeetingsCard({ d }: { d: J }) {
  const row = (m: J, soon: boolean) => (
    <li key={`${m.date}-${m.purpose}`} className="flex gap-2">
      <span className={cn('num w-24 shrink-0', soon ? 'font-semibold text-warn-ink' : 'text-muted-foreground')}>{String(m.date)}</span>
      <span className="min-w-0 text-ink">{String(m.purpose)}{m.link && <> · <a href={String(m.link)} target="_blank" rel="noopener noreferrer" className="text-brand-strong hover:underline">notice</a></>}</span>
    </li>
  );
  const up = arr(d.upcoming), recent = arr(d.recent);
  return (
    <Frame title="Board meetings" source={<>{String(d.source ?? '')}{freshness(d) && <> · {freshness(d)}</>}</>}>
      {up.length > 0 ? <ul className="space-y-1">{up.map((m) => row(m, true))}</ul> : <p className="text-muted-foreground">None announced ahead.</p>}
      {recent.length > 0 && <><p className="mt-2 mb-1 text-[11px] font-medium text-muted-foreground">Recent</p><ul className="space-y-1">{recent.map((m) => row(m, false))}</ul></>}
      {up.some((m) => /result/i.test(String(m.purpose))) && <p className="mt-2 text-[11px] text-warn-ink">A results meeting ahead is event risk: prices often gap on results.</p>}
    </Frame>
  );
}

function ActionsCard({ d }: { d: J }) {
  const up = arr(d.upcoming), past = arr(d.lastYear);
  const row = (a: J) => <li key={`${a.exDate}-${a.subject}`} className="flex gap-2"><span className="num w-24 shrink-0 text-muted-foreground">{isoDay(a.exDate)}</span><span className="text-ink">{String(a.subject)}</span></li>;
  return (
    <Frame title={`Corporate actions · ${d.symbol ?? ''}`} source={<>{String(d.source ?? '')}{freshness(d) && <> · {freshness(d)}</>}</>}>
      <p className="mb-1 text-[11px] font-medium text-muted-foreground">Ex-dates ahead</p>
      {up.length ? <ul className="space-y-1">{up.map(row)}</ul> : <p className="text-muted-foreground">None announced.</p>}
      {past.length > 0 && <><p className="mt-2 mb-1 text-[11px] font-medium text-muted-foreground">Last year</p><ul className="space-y-1">{past.slice(0, 6).map(row)}</ul></>}
    </Frame>
  );
}

function FilingsCard({ d }: { d: J }) {
  const fs = arr(d.filings).slice(0, 6);
  const press = arr((d.pressHeadlines as J | undefined)?.headlines).slice(0, 4);
  const [more, setMore] = useState(false);
  return (
    <Frame icon={<FileTextIcon className="size-3.5 text-muted-foreground" />} title="Filings and news" source="NSE corporate announcements (the company's own word) and Google News headlines (secondary)">
      <ul className="space-y-2">
        {(more ? fs : fs.slice(0, 3)).map((f, i) => (
          <li key={i} className="space-y-0.5">
            <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground"><span className="num">{String(f.time)}</span><span className="rounded bg-muted px-1.5">{String(f.category)}</span>{f.timing && <span>{typeof f.timing === 'string' ? f.timing : ''}</span>}</div>
            <p className="line-clamp-2 text-ink">{String(f.text)}</p>
            {f.pdf && <a href={String(f.pdf)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-brand-strong hover:underline">NSE filing <ExternalLinkIcon className="size-3" /></a>}
          </li>
        ))}
      </ul>
      {fs.length > 3 && <button type="button" className="mt-1.5 text-[11px] text-brand-strong" onClick={() => setMore((m) => !m)}>{more ? 'Fewer' : `${fs.length - 3} more filings`}</button>}
      {press.length > 0 && (
        <div className="mt-2.5 border-t pt-2">
          <p className="mb-1 text-[11px] font-medium text-muted-foreground">Press</p>
          <ul className="space-y-1">{press.map((h, i) => <li key={i}><a href={String(h.link)} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-brand-strong">{String(h.title)}</a> <span className="text-[11px] text-muted-foreground">{String(h.source)} · {String(h.time)}</span></li>)}</ul>
        </div>
      )}
    </Frame>
  );
}

const STATUS_CLS: Record<string, string> = { open: 'bg-brand-soft text-brand-strong', target: 'bg-bull-soft text-bull', stop: 'bg-bear-soft text-bear' };
function PaperCard({ d, act }: { d: J; act: CardActions }) {
  const ts = arr(d.trades).slice(0, 8);
  return (
    <Frame title="Your paper trades" right={`${num(d.count) ?? ts.length} in all`} source={<>Net after MoneyHQ&apos;s cost estimates; hypothetical. <Link href="/journal" className="text-brand-strong hover:underline">Journal</Link></>}>
      {ts.length === 0 && <p className="text-muted-foreground">No paper trades yet.</p>}
      <ul className="divide-y">
        {ts.map((t, i) => (
          <li key={i} className="flex flex-wrap items-center gap-2 py-1.5">
            <span className="font-semibold text-ink">{String(t.symbol)}</span>
            <span className={t.side === 'short' ? 'text-bear' : 'text-bull'}>{String(t.side)}</span>
            <span className={cn('rounded px-1.5 py-0.5 text-[10.5px] font-medium', STATUS_CLS[String(t.status)] ?? 'bg-muted text-muted-foreground')}>{String(t.status)}</span>
            <span className="text-[11px] text-muted-foreground">{String(t.setup)} · {String(t.placed)}</span>
            <span className="num ml-auto">{t.net_pnl ? <b className={tone(num(t.r))}>{String(t.net_pnl)}</b> : '—'}{num(t.r) ? <span className="text-muted-foreground"> · {t.r >= 0 ? '+' : ''}{t.r}R</span> : null}</span>
            <Chip onClick={() => act.ask(`Review my ${t.symbol} paper trade`)}>Review</Chip>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function CandleCard({ d }: { d: J }) {
  const c = (d.candle ?? null) as J | null;
  return (
    <Frame title="Intraday candle check" right={c ? `${c.symbol} · ${c.date} ${c.start}` : 'the setup as described'} source="Six years of NSE 5-minute data (495 stocks), entered at the next open, after 0.18% costs">
      {c?.state && <p className="mb-1.5 text-muted-foreground">Candle: {String(c.state)}</p>}
      <p className="text-ink">{String(d.line ?? '')}</p>
      {d.note && <p className="mt-1 text-[11px] text-muted-foreground">{String(d.note)}</p>}
    </Frame>
  );
}

/** A stock's daily chart in the conversation: the stock page's chart and engine (the new-high
 * breakout and its trailing stop, momentum rank, chart patterns), from the stored candles. */
export function ChartCard({ symbol, act, onReady, marks }: { symbol: string; act: CardActions } & ChartProps) {
  const h = useChartHistory(symbol, !!symbol, 0);
  const overlays = useOverlays(h, 'daily', true);
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<HistoryChart | null>(null);
  useEffect(() => {
    if (!el.current) return;
    const c = new HistoryChart(el.current, false);
    chart.current = c;
    return () => { c.destroy(); chart.current = null; };
  }, []);
  useEffect(() => {
    const c = chart.current;
    if (!c || !h.daily.length) return;
    c.setData(h.daily, false, true);
    c.setOverlays({ ...overlays, rank: [] }); // no rank pane here: the rank line would share the price scale
    c.showLast(250);
    onReady?.(symbol, h);
  }, [h, overlays]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { chart.current?.setMarks(marks ?? []); }, [marks, h.daily.length]);
  const last = h.daily[h.daily.length - 1];
  const st = useMemo(() => (h.daily.length ? setupStatus(h) : null), [h]);
  const forming = h.patterns.filter((p) => p.breakIdx === null).at(-1);
  return (
    <Frame icon={<CandlestickChartIcon className="size-3.5 text-muted-foreground" />} title={<Link href={`/stock/${encodeURIComponent(symbol)}`} className="hover:text-brand-strong">{symbol} · daily</Link>}
      right={last ? <>{px(last.c)} · {new Date(last.t * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' })}</> : h.loading ? 'loading…' : h.error ? 'unavailable' : ''}
      source="Stored NSE daily candles, adjusted for splits and bonuses; breakout markers and the trailing stop by MoneyHQ's tested rule">
      <div ref={el} className="h-64 w-full" />
      {h.error && <p className="text-bear">The chart couldn&apos;t load.</p>}
      {st && (
        <div className="mt-2 space-y-0.5 text-[11.5px] text-muted-foreground">
          {st.breakout && <p>{st.breakout}</p>}
          {st.momentum && <p>{st.momentum}</p>}
          {forming && <p>Forming: {forming.name} (tested: {PATTERN_RECORD[forming.name].edge === 'hint' ? 'a weak hint' : 'no edge'})</p>}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Link href={`/stock/${encodeURIComponent(symbol)}`} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-brand-strong hover:text-brand-strong">Full chart <ArrowUpRightIcon className="size-3" /></Link>
        <Chip onClick={() => act.ask(`Is ${symbol} in a tested setup now?`)}>Tested setup?</Chip>
        <Chip onClick={() => act.ask(`Why is ${symbol} moving today?`)}>Why is it moving?</Chip>
      </div>
    </Frame>
  );
}
